/**
 * Transform to Quote Modal
 *
 * Modal for converting an opportunity to an official quote
 */

import { useState } from 'react';
import { FileText, ArrowRight, Building2, DollarSign } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/lib/utils';
import type { PipelineOpportunity } from '../../types';

interface TransformToQuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: PipelineOpportunity | null;
  onConfirm: (projectName?: string) => Promise<void>;
  isLoading?: boolean;
}

export function TransformToQuoteModal({
  open,
  onOpenChange,
  opportunity,
  onConfirm,
  isLoading,
}: TransformToQuoteModalProps) {
  const [projectName, setProjectName] = useState('');

  const handleConfirm = async () => {
    await onConfirm(projectName || undefined);
  };

  const handleClose = () => {
    setProjectName('');
    onOpenChange(false);
  };

  if (!opportunity) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) setProjectName('');
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            Create Official Quote
          </DialogTitle>
          <DialogDescription>
            Transform this opportunity into an official quote. The opportunity will be archived and
            a new quote will be created.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Opportunity Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-900">{opportunity.title}</span>
            </div>

            {opportunity.account_name && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span>{opportunity.account_name}</span>
              </div>
            )}

            {opportunity.estimated_value != null && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span>{formatCurrency(opportunity.estimated_value, opportunity.currency || 'CAD')}</span>
              </div>
            )}
          </div>

          {/* Project Name Input */}
          <div className="space-y-2">
            <Label htmlFor="projectName">Project Name (Optional)</Label>
            <Input
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder={opportunity.title}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              Leave blank to use the opportunity title as the quote name
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              After creating the quote, you'll be redirected to add line items and finalize the
              pricing.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Quote'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
